/**
 * Конфигурация темы для создания палитры Material-UI на основе предоставленных токенов дизайна.
 * Этот файл расширяет интерфейсы палитры и темы Material-UI, чтобы включить пользовательские свойства.
 */

import { Tokens } from '../types';

/**
 * Расширяет интерфейс TypeText Material-UI для включения свойства цвета tertiary.
 */
declare module '@mui/material/styles/createPalette' {
  interface TypeText {
    primary: string;
    secondary: string;
    disabled: string;
    hint: string;
    tertiary: string;
  }

  /**
   * Расширяет интерфейс TypeBackground Material-UI для включения дополнительных цветов фона.
   */
  interface TypeBackground {
    default: string;
    paper: string;
    primary: string;
    dark: string;
    lightPrimary: string;
    lightSecondary: string;
    darkPrimary: string;
    darkSecondary: string;
    greyscalePrimary: string;
    greyscaleSecondary: string;
    greyscaleTertiary: string;
    gradientPurple: string;
  }

  /**
   * Расширяет интерфейс PaletteOptions Material-UI для включения пользовательских цветовых схем review и blue.
   */
  interface PaletteOptions {
    review: {
      main: string;
      light: string;
      stroke: string;
    };
    blue: {
      main: string;
      light: string;
      stroke: string;
    };
    stroke: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
  }

  /**
   * Расширяет интерфейс Palette Material-UI для включения пользовательских цветовых схем review и blue.
   */
  interface Palette {
    review: {
      main: string;
      light: string;
      stroke: string;
    };
    blue: {
      main: string;
      light: string;
      stroke: string;
    };
    stroke: {
      primary: string;
      secondary: string;
      tertiary: string;
    };
  }

  /**
   * Расширяет интерфейс SimplePaletteColorOptions Material-UI для включения дополнительных цветовых свойств.
   */
  interface SimplePaletteColorOptions {
    light?: string;
    main: string;
    dark?: string;
    contrastText?: string;
    stroke?: string;
    hover?: string;
    black?: string;
    quaternary?: string;
  }

  /**
   * Расширяет интерфейс PaletteColor Material-UI для включения дополнительных цветовых свойств.
   */
  interface PaletteColor {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
    stroke: string;
    hover: string;
    black: string;
    quaternary: string;
  }
}

/**
 * Расширяет интерфейс ThemeOptions Material-UI для включения токенов дизайна и функции px.
 */
declare module '@mui/material/styles' {
  export interface ThemeOptions {
    tokens: Tokens;
    px: (px: number) => number;
  }

  /**
   * Расширяет интерфейс Theme Material-UI для включения токенов дизайна и функции px.
   */
  export interface Theme {
    tokens: Tokens;
    px: (px: number) => number;
  }
}

/**
 * Создает палитру Material-UI с использованием предоставленных токенов дизайна.
 *
 * @param tokens - Токены дизайна для создания палитры.
 * @returns Созданный объект палитры.
 */
export const createPalette = (tokens: Tokens) => {
  return {
    primary: {
      main: tokens.colors.original.brand.primary,
      dark: tokens.colors.original.brand.tertirary,
      light: tokens.colors.original.brand.secondary,
      contrastText: tokens.colors.text.dark.primary,
      quaternary: tokens.colors.original.brand.quaternary,
    },
    secondary: {
      main: tokens.colors.accent.text.error,
      contrastText: tokens.colors.text.dark.primary,
    },
    error: {
      main: tokens.colors.accent.text.error,
      light: tokens.colors.accent.bg.error,
      stroke: tokens.colors.accent.stroke.error,
      hover: tokens.colors.stroke.hover,
      dark: tokens.colors.stroke.error,
    },
    success: {
      main: tokens.colors.accent.text.success,
      light: tokens.colors.accent.bg.success,
      stroke: tokens.colors.accent.stroke.success,
    },
    review: {
      main: tokens.colors.accent.text.review,
      light: tokens.colors.accent.bg.review,
      stroke: tokens.colors.accent.stroke.review,
    },
    warning: {
      main: tokens.colors.accent.text.warning,
      light: tokens.colors.accent.bg.warning,
      stroke: tokens.colors.accent.stroke.warning,
    },
    blue: {
      main: tokens.colors.accent.text.blue,
      light: tokens.colors.accent.bg.blue,
      stroke: tokens.colors.accent.stroke.blue,
    },
    background: {
      paper: tokens.colors.bg.light.primary,
      default: tokens.colors.bg.light.secondary,
      primary: tokens.colors.original.brand.secondary,
      lightPrimary: tokens.colors.bg.light.primary,
      lightSecondary: tokens.colors.bg.light.secondary,
      darkPrimary: tokens.colors.bg.dark.primary,
      darkSecondary: tokens.colors.bg.dark.secondary,
      greyscalePrimary: tokens.colors.bg.greyscale.primary,
      greyscaleSecondary: tokens.colors.bg.greyscale.secondary,
      greyscaleTertiary: tokens.colors.bg.greyscale.tertirary,
      gradientPurple: '#8349ff', // TODO - исправить нет в токенах
    },
    text: {
      primary: tokens.colors.text.light.primary,
      secondary: tokens.colors.text.light.secondary,
      tertiary: tokens.colors.text.light.tertirary,
      disabled: tokens.colors.text.light.tertirary,
    },
    stroke: {
      primary: tokens.colors.stroke.primary,
      secondary: tokens.colors.stroke.secondary,
      tertiary: tokens.colors.stroke.tertirary,
      error: tokens.colors.stroke.error,
      hover: tokens.colors.stroke.hover,
    },
  } as const;
};
